/**
 * Relative day labels for the conversation list (plan §3.5, §10).
 * Framework-free (copied to next-mapit); never touches the global `moment`
 * locale.
 *
 * Dates are always Gregorian: `ar-SA` defaults to the Umm al-Qura (Hijri)
 * calendar in every ICU version (C19). The digits follow the locale
 * (Arabic-Indic for `ar-SA`), pending product question Q4.
 */

/** Labels the caller translates (`dates.today`, `dates.yesterday`). */
export const RELATIVE_DAYS = Object.freeze({
  TODAY: "today",
  YESTERDAY: "yesterday",
});

const DAY_MS = 24 * 60 * 60 * 1000;

const FORMAT_OPTIONS = Object.freeze({
  /**
   * What the user reads. A short month name rather than `dateStyle:
   * 'medium'`, which is all digits in Arabic (٠٥‏/٠٣‏/٢٠٢٦); in English both
   * give "Mar 5, 2026".
   */
  label: Object.freeze({
    year: "numeric",
    month: "short",
    day: "numeric",
    calendar: "gregory",
  }),
  /** Latin-digit parts, only used to find the calendar day in a time zone. */
  dayKey: Object.freeze({
    year: "numeric",
    month: "numeric",
    day: "numeric",
    calendar: "gregory",
    numberingSystem: "latn",
  }),
});

/**
 * The `calendar` option is ignored by older browsers; the `-u-ca-` locale
 * extension works wherever Intl does. A tag that already has a `-u-`
 * extension keeps it (the option still applies).
 */
const gregorianLocale = (lang) =>
  /-u-/i.test(lang) ? lang : `${lang}-u-ca-gregory`;

const formatters = new Map();

/**
 * A cached `Intl.DateTimeFormat`. An invalid time zone falls back to the
 * runtime's zone and an invalid language to the runtime's locale, so a bad
 * value never breaks the list.
 */
const getFormatter = (kind, lang, timeZone) => {
  const key = `${kind}|${lang}|${timeZone}`;
  if (!formatters.has(key)) {
    const locales = lang ? [gregorianLocale(lang), undefined] : [undefined];
    const zones = timeZone ? [timeZone, undefined] : [undefined];
    let formatter = null;
    for (const zone of zones) {
      for (const locale of locales) {
        try {
          formatter = new Intl.DateTimeFormat(locale, {
            ...FORMAT_OPTIONS[kind],
            timeZone: zone,
          });
          break;
        } catch {
          // RangeError: try the next fallback
        }
      }
      if (formatter) break;
    }
    formatters.set(key, formatter);
  }
  return formatters.get(key);
};

/** Days since the epoch of the calendar day `date` falls on in `timeZone`. */
const dayNumber = (date, timeZone) => {
  const parts = getFormatter("dayKey", "en-US", timeZone).formatToParts(date);
  const value = (type) =>
    Number(parts.find((part) => part.type === type)?.value);
  return Date.UTC(value("year"), value("month") - 1, value("day")) / DAY_MS;
};

const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * `'today'`, `'yesterday'` (calendar days in `timeZone`, not 24-hour
 * windows) or the localized Gregorian date ("Mar 5, 2026" / "٥ مارس ٢٠٢٦").
 * A time after `now` can only be clock skew between the server and this
 * device, so it reads as today.
 *
 * @param {string | number | Date} iso
 * @param {object} [options]
 * @param {number | Date} [options.now] defaults to `Date.now()`
 * @param {string} [options.lang] BCP 47 tag, e.g. `i18n.language`
 * @param {string} [options.timeZone] IANA zone; defaults to the runtime's
 * @returns {string | null} null when `iso` isn't a valid date
 */
export function formatRelativeDay(
  iso,
  { now = Date.now(), lang, timeZone } = {},
) {
  const date = toDate(iso);
  const today = toDate(now);
  if (!date || !today) return null;

  const daysAgo = dayNumber(today, timeZone) - dayNumber(date, timeZone);
  if (daysAgo <= 0) return RELATIVE_DAYS.TODAY;
  if (daysAgo === 1) return RELATIVE_DAYS.YESTERDAY;
  return getFormatter("label", lang, timeZone).format(date);
}
